"use client";

import { useEffect, useState } from "react";
import { Product, Variant, VariantGroup } from "../../types/types";
import { ColourOption, ColoursUI } from "./ColoursUI";
import { StandardProps } from "../componentMap";
import { useVariant } from "../../hooks/useVariant/useVariant";
import { hasSizes } from "../../utils/product/hasSizes";
import { createSelectedColourUrlObj } from "../../utils/routing/createSelectedColourUrl";
import { useRouter } from "next/router";
import {
  isVariantReducedToClear,
  isVariantPromotional,
  isAllVariantsReduced,
  isAllVariantsSave,
  isAllVariantsReducedForColour,
  isSomeVariantsReducedForColour,
  isAllVariantsSaveForColour,
  isSomeVariantsSaveForColour,
} from "../../utils/product/promotionUtils";

const isMobileWidth = () => window.innerWidth < 768;

export const Colours = ({
  product,
  experiences,
}: Pick<StandardProps, "product" | "experiences">) => {
  const { variant } = useVariant(product);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();
  const { saveLabels } = experiences;

  useEffect(() => {
    setIsMobile(isMobileWidth());
    const handleResize = () =>
      isMobileWidth() ? setIsMobile(true) : setIsMobile(false);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const variantGroupsHaveColours = !!product.variantGroups?.[0]?.value;
  if (!variantGroupsHaveColours) return null;

  const colourOptions: ColourOption[] =
    product.variantGroups?.map((variantGroup: VariantGroup) => {
      const colour = variantGroup.value || "";

      const groupVariants = getVariantsForGroup(variantGroup, product);

      const { showReduced, label } = getColourReductionLabelInfo(
        product,
        groupVariants[0],
        variantGroup,
        saveLabels === "on",
      );

      return {
        colour,
        showAsAvailable: groupVariants.some(
          (v) =>
            v.availability?.availableToOrder ||
            v.availability?.unavailableReason === "inStoreOnly" ||
            v.availability?.unavailableReason === "notYetAvailable",
        ),
        colourSwatch: groupVariants[0]?.colourSwatch,
        canonicalURL: variantGroup.canonicalURL,
        showReduced,
        reducedLabel: label,
        selected: variant.differentiators?.colour === colour,
        url: createSelectedColourUrlObj(colour, product, router.query),
      };
    }) || [];

  if (colourOptions?.length <= 1) return null;

  const sortedColourOptions = sortColourOptions(colourOptions);

  const rowsToDisplay = getItemsCountToDisplay(
    isMobile,
    sortedColourOptions,
    variant,
  );

  return (
    <ColoursUI
      colourOptions={sortedColourOptions}
      rowsToDisplay={rowsToDisplay}
    />
  );
};

const getColourReductionLabelInfo = (
  product: Product,
  variant: Variant | undefined,
  variantGroup: VariantGroup,
  showSaveLabels: boolean,
) => {
  const allVariantsReduced = isAllVariantsReduced(product);

  const allVariantsSave = isAllVariantsSave(product);

  if (allVariantsReduced || allVariantsSave) {
    return { showReduced: false, label: "" };
  }

  if (!hasSizes(product) && variant) {
    const isVariantReduced = isVariantReducedToClear(variant);
    const isVariantSave = isVariantPromotional(variant);

    return {
      showReduced: isVariantReduced || isVariantSave,
      label: isVariantReduced
        ? "Reduced"
        : isVariantSave && showSaveLabels
          ? "Offer"
          : "",
    };
  }

  const someVariantsReducedForColour =
    isSomeVariantsReducedForColour(variantGroup);

  const allVariantsReducedForColour =
    isAllVariantsReducedForColour(variantGroup);

  const someVariantsSaveForColour = isSomeVariantsSaveForColour(variantGroup);

  const allVariantsSaveForColour = isAllVariantsSaveForColour(variantGroup);

  if (someVariantsReducedForColour) {
    return { showReduced: true, label: "Selected sizes reduced" };
  }

  if (allVariantsReducedForColour) {
    return { showReduced: true, label: "Reduced" };
  }

  if (someVariantsSaveForColour && showSaveLabels) {
    return { showReduced: true, label: "Selected sizes offer" };
  }

  if (allVariantsSaveForColour && showSaveLabels) {
    return { showReduced: true, label: "Offer" };
  }

  return { showReduced: false, label: "" };
};

const getItemsCountToDisplay = (
  isMobile: boolean,
  colourOptions: ColourOption[],
  selectedVariant: Variant,
) => {
  const selectedColourIndex = colourOptions.findIndex(
    (colour) => colour.colour === selectedVariant.differentiators!.colour,
  );

  const ceil = isMobile
    ? Math.ceil((selectedColourIndex + 1) / 2) * 2
    : Math.ceil((selectedColourIndex + 1) / 3) * 3;
  if (selectedColourIndex >= 9) return ceil;

  if (isMobile) return 10;

  return 9;
};

function sortColourOptions(colours: ColourOption[]) {
  const colourOptions = [...colours];

  const colourOptionsAvailable = colourOptions.filter(
    (colourOption) => colourOption.showAsAvailable,
  );

  const colourOptionsNotAvailable = colourOptions.filter(
    (colourOption) => !colourOption.showAsAvailable,
  );

  const combinedColourOptions = [
    ...colourOptionsAvailable,
    ...colourOptionsNotAvailable,
  ];

  return combinedColourOptions;
}

function getVariantsForGroup(variantGroup: VariantGroup, product: Product) {
  const groupVariantIds = variantGroup.variants.map((v) => v.id);
  const allVariants: Variant[] = product.variants;
  const groupVariants = allVariants.filter((v) =>
    groupVariantIds.includes(v.id),
  );
  return groupVariants;
}
